import { useEffect, useState } from "react"
import type { PhraseLanguage } from "src/intl"

export type Translations = Record<string, string>

const BASE_LOCALES: Record<string, PhraseLanguage> = {
	"da-DK": "da",
	"de-DE": "de",
	"en-GB": "en",
	"es-ES": "es",
	"fi-FI": "fi",
	"fr-FR": "fr",
	"it-IT": "it",
	"nl-NL": "nl",
	"pt-PT": "pt",
}

export function useTranslations(locale: string) {
	const [isLoaded, setIsLoaded] = useState(false)
	const [translations, setTranslations] = useState<Translations | undefined>(undefined)

	useEffect(() => {
		let isCancelled = false

		async function loadTranslations() {
			setIsLoaded(false)
			const translationsData = await fetchTranslations(locale)
			if (!isCancelled) {
				setTranslations(translationsData)
				setIsLoaded(true)
			}
		}

		void loadTranslations()

		return () => {
			isCancelled = true
		}
	}, [locale])

	return { translations, translationsLoaded: isLoaded }
}

async function fetchTranslations(locale: string): Promise<Translations | undefined> {
	// Fetch base locales directly instead of trying to fetch e.g. "de-DE" first before getting "de" translations
	const phraseLocale = BASE_LOCALES[locale] ?? locale

	const url = `https://translations.dealer.finder.porsche.com/ui/${phraseLocale}.json`

	return fetch(url, {
		headers: { accept: "application/json" },
	}).then((response) => {
		if (response.status === 404 && locale !== "default") {
			// fetch fallback locale
			const localeParts = phraseLocale.split("-")
			if (localeParts.length > 1) {
				return fetchTranslations(localeParts[0])
			}
			return fetchTranslations("default")
		}
		return response.status >= 200 && response.status < 300 ? response.json() : undefined
	})
}
